/**
 * Perks' own vocabulary, mapped onto the panel-wide design tokens in
 * component/common/designSystem/theme.js.
 *
 * Only domain meaning lives here — palettes, chart defaults and the chip
 * classes themselves belong to the design system.
 */
import {
  CHART_COLORS,
  toneChip,
  toneColor,
  toneColors,
  toneLookup
} from '../../component/common/designSystem';

/**
 * Scheme lifecycle, shared by Salary Sacrifice and Health Scheme (both use the
 * same 1–4 enum). A verdict rather than a scale, so each bucket takes its
 * semantic tone. Order matches the backend's Pending → Suspended sequence.
 */
export const SCHEME_STATUS_COLORS = toneColors(['warning', 'success', 'neutral', 'danger']);

const schemeStatusTone = toneLookup({
  'pending approval': 'warning',
  active: 'success',
  inactive: 'neutral',
  suspended: 'danger',
  // The EAP plan reuses this chip with its own status words.
  approved: 'success',
  'sent for approval': 'warning',
  rejected: 'danger',
  disabled: 'neutral',
  // Custom perk statuses.
  pending: 'warning',
  draft: 'info',
  // Payment.
  paid: 'success',
  'in progress': 'warning',
  failed: 'danger'
});

export const schemeStatusColor = (label) => toneColor(schemeStatusTone(label));
export const schemeStatusChipClass = (label) => toneChip(schemeStatusTone(label));

/** Shoorah provided vs Company provided — a source, not a ranking. */
export const SCHEME_TYPE_COLORS = toneColors(['brand', 'accent']);

/** Added by Shoorah vs added by you. */
export const ENROLLED_BY_COLORS = toneColors(['brand', 'teal']);

/** Health cash plans vs occupational health plans. */
export const PLAN_MIX_COLORS = toneColors(['info', 'teal']);

/**
 * Ordered ramps for the distribution charts. Each is a sequence rather than a
 * set of categories, so it gets a ramp instead of the categorical palette —
 * and each has exactly as many colours as its band list has bands, so a slice
 * never borrows its neighbour's meaning.
 */

/**
 * Runway before a scheme lapses: Expired → Over 6 months. Red at the expired
 * end because that band is the one that needs acting on, not celebrating.
 */
export const EXPIRY_RAMP = [
  CHART_COLORS.red,
  CHART_COLORS.orange,
  CHART_COLORS.amber,
  CHART_COLORS.blue,
  CHART_COLORS.greenDeep
];

/**
 * How long somebody has been on a scheme: Under a month → Over a year. Longer
 * is steadier, so this runs a plain new → established ramp.
 */
export const TENURE_RAMP = [
  CHART_COLORS.brandSoft,
  CHART_COLORS.brand,
  CHART_COLORS.teal,
  CHART_COLORS.greenDeep,
  CHART_COLORS.green
];

/**
 * One counselling call: Under 15 min → Over 2 hours. Length is not quality
 * here, just length, so this runs cool → warm rather than bad → good.
 */
export const CALL_DURATION_RAMP = [
  CHART_COLORS.brandSoft,
  CHART_COLORS.brand,
  CHART_COLORS.teal,
  CHART_COLORS.amber,
  CHART_COLORS.orange
];

/** Calls placed per person: 1 → 8+. */
export const CALLS_PER_USER_RAMP = [
  CHART_COLORS.brandSoft,
  CHART_COLORS.brand,
  CHART_COLORS.greenDeep,
  CHART_COLORS.green
];

/**
 * Clicks on a custom perk: Never opened → 11+. Grey for the people who hold a
 * perk and never opened it — that band is an absence, not a low score, and
 * colouring it red would read as a failure by the people rather than the perk.
 */
export const PERK_CLICKS_RAMP = [
  CHART_COLORS.gray,
  CHART_COLORS.brandSoft,
  CHART_COLORS.brand,
  CHART_COLORS.greenDeep,
  CHART_COLORS.green
];

/**
 * How many of the four programmes one person is on: None → All four. "None" is
 * grey rather than red for the same reason — it is the thing to fix, but the
 * chart is about coverage, not blame.
 */
export const PROGRAMME_COUNT_RAMP = [
  CHART_COLORS.gray,
  CHART_COLORS.brandSoft,
  CHART_COLORS.brand,
  CHART_COLORS.greenDeep,
  CHART_COLORS.green
];

/** Per-programme accent, so a programme keeps one colour across every page. */
export const PROGRAMME_COLORS = {
  instant_support: CHART_COLORS.blue,
  salary_sacrifice: CHART_COLORS.brand,
  health_scheme: CHART_COLORS.teal,
  custom_perks: CHART_COLORS.pink
};

export const programmeColor = (key) => PROGRAMME_COLORS[key] || CHART_COLORS.gray;

/**
 * Days-to-expiry → a tone, for the "expires in N days" chips. Thresholds match
 * the EXPIRY_BANDS the backend groups by, so a chip and the chart agree.
 */
export const expiryChipClass = (daysToExpiry) => {
  if (daysToExpiry == null) return toneChip('neutral');
  if (daysToExpiry < 0) return toneChip('danger');
  if (daysToExpiry < 31) return toneChip('warning');
  if (daysToExpiry < 91) return toneChip('info');
  return toneChip('success');
};

/** "Expired", "Expires in 12 days", "Expires in 5 months". */
export const expiryLabel = (daysToExpiry) => {
  if (daysToExpiry == null) return 'No expiry set';
  if (daysToExpiry < 0) return `Expired ${Math.abs(daysToExpiry)} days ago`;
  if (daysToExpiry === 0) return 'Expires today';
  if (daysToExpiry < 60) return `Expires in ${daysToExpiry} days`;
  return `Expires in ${Math.round(daysToExpiry / 30)} months`;
};
